import {
  Component,
  ComponentInterface,
  Event,
  EventEmitter,
  h,
  Listen,
  Prop,
  State,
} from '@stencil/core';

import guid from '../../utils/guid';
import { InterfacePafInput } from './paf-text-input.custom';

@Component({
  shadow: false,
  styleUrl: 'paf-text-input.scss',
  tag: 'paf-text-input',
})

export class PafTextInput implements ComponentInterface {

  /**
   * This value will be used as aria-activedescendant, to anounce the selected
   * list item if this input is used inside of an autocomplete.
   */
  @Prop() public autoCompleteIndex?: string;

  /**
   * The floating label for the input field
   */
  @Prop() public label!: string;

  /**
   * The name attribute for the input
   */
  @Prop() public name!: string;

  /**
   * The type attribute for the input. Allowed values: text, date, time
   */
  @Prop() public type!: InterfacePafInput['type'];

  /**
   * The placeholder for the input field.
   */
  @Prop() public placeholder?: string;

  /**
   * If provided, the input's value will be set to this
   *
   * Important: if you use Date or Time, you should provide a value. Different
   * browsers render inputs of these types very differently. By Setting a
   * value you can mitigate some of the arising issues.
   */
  @Prop({
    mutable: true,
    reflect: true,
  }) public value?: string;

  /**
   * Determines if the input is required
   */
  @Prop() public required? = false;

  /**
   * Each input needs to have an individual id.
   * If no id is provided, the component will
   * create a unique id by itself.
   */
  @Prop() public inputId?: string;

  /**
   * Error message to show if the input is required and turns invalid
   */
  @Prop() public errorMessage? = '';

  /**
   * If type date or time is used, you can provide a min value.
   * Example min date: 2018-07-22 (YYYY-MM-DD)
   * Example min time: 09:00
   */
  @Prop() public min?: string;

  /**
   * If type date or time is used, you can provide a max value
   * Example min date: 2018-07-22 (YYYY-MM-DD)
   * Example min time: 09:00
   */
  @Prop() public max?: string;

  /**
   * If you use type date or time, you should provide a description
   * for the calender-/time-picker icon in the input
   */
  @Prop() public dateTimeIconDescription?: string;

  @State() private _isValid = false;
  @State() private _touched = false;

  @Listen('demand-focus')
  public handleFocusDemandFromOutside(): void {
    console.log('focus');

    this._input.focus();
    this._input.select();
  }

  /**
   * The native input event
   */
  @Event({
    bubbles: true,
    composed: true,
    eventName: 'paf-text-input_input',
  })
  public inputEvent: EventEmitter<string>;

  /**
   * The native keyup event
   */
  @Event({
    bubbles: true,
    composed: true,
    eventName: 'paf-text-input_key-up',
  })
  public keyupEvent: EventEmitter<string>;

  private _input: HTMLInputElement;
  private _id = '';

  private _handleKeyUp = (): void => {
    this.value = this._input.value;

    if (this.type === 'date' || this.type === 'time') {
      this._validate();
    }

    this.keyupEvent.emit(this._input.value);
  };

  private _handleInput = (evt: InputEvent): void => {
    evt.stopImmediatePropagation();
    evt.preventDefault();

    this.value = this._input.value;
    this._validate();
    this.inputEvent.emit(this._input.value);
  };

  private _validate = (): void => {
    this._touched = true;
    this._isValid = this._input.checkValidity();
  };

  private _shouldShowError = (): boolean => !this._isValid && this.required && this._touched;

  private _handleFocus = (): void => {
    this._input.focus();
  };

  private _onDateTimeIconClick = (evt): void => {
    evt.preventDefault();

    // does work for desktop browsers and android
    this._input.showPicker();

    // does work for ios
    this._input.focus();
  };

  public componentWillLoad(): void {
    if (this.inputId) {
      this._id = this.inputId;
    } else {
      this._id = `input-${guid()}`;
    }
  }

  public componentDidLoad(): void {
    this._input.addEventListener('focus', this._handleFocus);
  }

  public disconnectCallback(): void {
    this._input.removeEventListener('focus', this._handleFocus);
  }

  public render(): HTMLDivElement {
    const isTime = this.type === 'time';
    const isDate = this.type === 'date';
    const inputAttributes = {};

    if (this.min) {
      inputAttributes['min'] = this.min;
    }

    if (this.max) {
      inputAttributes['max'] = this.max;
    }

    if (this._shouldShowError()) {
      inputAttributes['aria-invalid'] = true;
    }

    if (!(isTime || isDate)) {
      inputAttributes['placeholder'] = this.placeholder || this.label;
    }

    const listAttributes = {};

    if (this.autoCompleteIndex) {
      listAttributes['aria-activedescendant'] = this.autoCompleteIndex;
    }

    return (
      <div>
        <div class={{
          'paf-text-input': true,
          'paf-text-input--has-error': this._shouldShowError(),
          'paf-text-input--has-error-icon': this._shouldShowError(),
          'paf-text-input--has-icon': this.type === 'date' || this.type === 'time',
          'paf-text-input--no-placeholder': !this.placeholder,
        }}>
          <input
            {...listAttributes}
            ref={(el): void => {
              this._input = el as HTMLInputElement;
            }}
            {...inputAttributes}
            class='paf-text-input__input'
            autocomplete='off'
            aria-describedby={`error-${this._id}`}
            type={this.type}
            id={this._id}
            aria-required={this.required}
            required={this.required}
            value={this.value || ''}
            name={this.name}
            data-testid='input'
            onInput={this._handleInput}
            onKeyUp={this._handleKeyUp}
          />
          <label
            class='paf-text-input__label'
            htmlFor={this._id}
          >
            {this.label}
          </label>

          <svg
            class='paf-text-input__error-icon'
          >
            <path d='M14.667 4h2.667v18.667h-2.667V4ZM14.667 25.333h2.667V28h-2.667v-2.667Z'/>
          </svg>

          {(isTime || isDate) &&
            <button
              class='paf-text-input__supplementary-button'
              onClick={this._onDateTimeIconClick}
              type='button'
              aria-haspopup='true'
              aria-label={this.dateTimeIconDescription}
            >
              {isTime &&
                <svg
                  class='paf-text-input__supplementary-icon'
                  viewBox='0 0 29 32'
                >
                  <path d='m28.1 9.6.9-1L23.3 3l-.9.9 2.4 2.3-1.9 1.9c-2.2-2-5.1-3.2-8.2-3.4V2.2H18V.8h-8v1.3h3.3v2.7C6.3 5.2.7 11 .7 18.2c0 7.3 5.9 13.3 13.3 13.3s13.3-6 13.3-13.3c0-3.5-1.3-6.6-3.5-9l1.9-1.9 2.4 2.3ZM26 18.2c0 6.6-5.4 12-12 12s-12-5.4-12-12 5.4-12 12-12 12 5.3 12 12Zm-11.3-6.7h-1.3v-4h1.3v4Zm-11.4 6h4v1.3h-4v-1.3Zm17.4 0h4v1.3h-4v-1.3Zm-7.4 7.3h1.3v4h-1.3v-4Zm8.5-13.5-.9-.9-5.5 5.5c-.4-.3-.9-.4-1.4-.4-1.5 0-2.7 1.2-2.7 2.7 0 1.5 1.2 2.7 2.7 2.7 1.5 0 2.7-1.2 2.7-2.7 0-.5-.1-1-.4-1.3l5.5-5.6ZM14 19.5c-.7 0-1.3-.6-1.3-1.3 0-.7.6-1.3 1.3-1.3.7 0 1.3.6 1.3 1.3 0 .7-.6 1.3-1.3 1.3Z' />
                </svg>
              }

              {isDate &&
                <svg
                  class='paf-text-input__supplementary-icon'
                  viewBox='0 0 30 31'
                >
                  <path d='M11.1 23.8c-.8 0-1.6-.1-2.1-.4l.1-1.2c.5.3 1.2.5 2 .5 1.1 0 2.3-.4 2.3-1.6 0-1.3-1.1-1.7-2.3-1.7h-.9v-1.2h.8c.9 0 2.3-.3 2.3-1.5 0-1.1-.8-1.5-1.9-1.5-.8 0-1.5.3-2 .5l-.1-1.2c.7-.3 1.6-.4 2.3-.4 1.6 0 3.1.8 3.1 2.5 0 1.2-.9 1.9-2 2.1 1.2.3 2.1.9 2.1 2.3-.1 2.2-1.9 2.8-3.7 2.8Zm8.2-8v7.7h1.3v-9.3h-1.2l-2.3 1.9.7.9 1.5-1.2ZM29.7 2.2v28H.3v-28h5.3V.8H7v1.3h16V.8h1.3v1.3h5.4v.1Zm-1.4 8H1.7v18.7h26.7V10.2h-.1Zm0-6.7h-4v1.3H23V3.5H7v1.3H5.7V3.5h-4v5.3h26.7V3.5h-.1Z' />
                </svg>
              }
            </button>
          }

        </div>

        {this.errorMessage && this._shouldShowError() &&
          <span
            aria-live='assertive'
            class='paf-text-input__error-message'
            id={`error-${this._id}`}
            role='alert'
            data-testid='error'
          >{this.errorMessage}</span>
        }
      </div>
    );
  }
}
